import { DateTime } from 'luxon'
import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as publicationEditionActions from '@redux/modules/publicationEdition'

import defaultFormState, { requiredFields } from '@models/publicationEdition'

import { getDeadlineStatuses, mapPublicationEditionToModel } from '@functions/publicationEdition'
import { baseURL } from '@functions/api'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { PublicationEditionModel, PublicationEditionRequestOptions } from '@models/types'

const dateNow = DateTime.now()

export const generateUrls = (publicationEdition?: Partial<PublicationEditionModel>) => {
  const { id } = publicationEdition || {}

  return {
    inspectionsCsvUrl: `${baseURL}/publication_editions/${id}/inspection_csv.json`,
  }
}

type AutoGenerateParams = {
  dispatch: AppDispatch,
  publicationEdition: PublicationEditionModel,
}

const autoGenerate = (params: AutoGenerateParams) => {
  const { dispatch, publicationEdition } = params
  const { autoGenerate: autoGenerateFn } = publicationEditionActions

  return dispatch(autoGenerateFn(publicationEdition.publication_id)).then((result) => {
    // @ts-expect-error TS2339
    const { data, success } = result
    // @ts-expect-error TS2339
    if (success && data?.url) window.open(data.url, '_blank')

    return result
  })
}

type CreatePublicationEditionParams = {
  dispatch: AppDispatch,
  publicationEditionParams: Partial<PublicationEditionModel>,
  requestOptions?: PublicationEditionRequestOptions,
}

const createPublicationEdition = (params: CreatePublicationEditionParams) => {
  const { dispatch, publicationEditionParams, requestOptions } = params
  const { createPublicationEdition: createFn } = publicationEditionActions

  // @ts-expect-error TS2345
  return dispatch(createFn(publicationEditionParams, requestOptions))
}

type UpdatePublicationEditionParams = {
  dispatch: AppDispatch,
  publicationEdition: PublicationEditionModel,
  publicationEditionParams: Partial<PublicationEditionModel>,
  requestOptions?: PublicationEditionRequestOptions,
}

const updatePublicationEdition = (params: UpdatePublicationEditionParams) => {
  const {
    dispatch, publicationEdition, publicationEditionParams, requestOptions,
  } = params
  const { updatePublicationEdition: updateFn } = publicationEditionActions

  const updatedParams = {
    id: publicationEdition.id,
    ...publicationEditionParams,
  }

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeletePublicationEditionParams = {
  dispatch: AppDispatch,
  publicationEdition: DeleteParams<PublicationEditionModel>,
}

const deletePublicationEdition = (params: DeletePublicationEditionParams) => {
  const { dispatch, publicationEdition } = params
  const { deletePublicationEdition: deleteFn } = publicationEditionActions

  // @ts-expect-error TS2345
  return dispatch(deleteFn(publicationEdition))
}

type RebuildDeadlinesParams = {
  dispatch: AppDispatch,
  publicationEdition: PublicationEditionModel,
}

const rebuildDeadlines = (params: RebuildDeadlinesParams) => {
  const { publicationEdition, dispatch } = params
  const { rebuildDeadlines: rebuildDeadlinesFn } = publicationEditionActions

  return dispatch(rebuildDeadlinesFn(publicationEdition.publication_id))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function usePublicationEditionForm(
  publicationEdition: Partial<PublicationEditionModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const publicationEditionForm = useForm(
    defaultFormState,
    {
      // @ts-expect-error TS2345
      entity: mapPublicationEditionToModel(publicationEdition),
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [publicationEdition.id, publicationEdition.cache_key],
  )

  return {
    ...publicationEditionForm,
  }
}

export const useRelations = (publicationEdition: Partial<PublicationEditionModel> = {}) => {
  const { publication_id } = publicationEdition

  const { publications } = useSelector(reduxState => reduxState.entities)

  const publication = publication_id && publications[publication_id]
    ? publications[publication_id] : {}

  return {
    publication,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: PublicationEditionRequestOptions,
}

function usePublicationEdition(initEntity: Partial<PublicationEditionModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: publicationEdition }: { entity: PublicationEditionModel} = useLatestEntity(initEntity, 'publicationEditions')
  const { dates, id, publication_date = '' } = publicationEdition

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { publication } = useRelations(publicationEdition)

  useReduxAction(
    'publicationEditions',
    'loadPublicationEdition',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    // @ts-expect-error TS2339
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.publicationEditions)

  const publicationDateObj = DateTime.fromFormat(publication_date, 'dd/MM/yyyy')

  const bookingDeadlineString = digObject(dates, 'booking.date_time', '')
  const bookingDeadlineObj = DateTime.fromFormat(bookingDeadlineString, 'dd/MM/yyyy HH:mm')
  const bookingDeadlinePassed = bookingDeadlineObj ? bookingDeadlineObj < dateNow : true

  const deadlines = digObject(publicationEdition, 'deadlines', {})
  const deadlineStatuses = getDeadlineStatuses(publicationEdition)

  const hasArtworkDeadline = !!digObject(deadlines, 'artwork')
  const hasBookingDeadline = !!digObject(deadlines, 'booking')
  const hasEditorialDeadline = !!digObject(deadlines, 'editorial')
  const hasOfiDeadline = !!digObject(deadlines, 'ofi')

  return {
    bookingDeadlineObj,
    bookingDeadlinePassed,
    callbacks: {
      autoGenerate: () => autoGenerate({ dispatch, publicationEdition }),
      createPublicationEdition: (
        publicationEditionParams: Partial<PublicationEditionModel>,
        entityOptions?: PublicationEditionRequestOptions,
      ) => (
        createPublicationEdition({ publicationEditionParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditPublicationEdition: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditPublicationEditionModal',
        payload: { publicationEdition },
      }),
      deletePublicationEdition: () => deletePublicationEdition({ dispatch, publicationEdition }),
      rebuildDeadlines: () => rebuildDeadlines({ dispatch, publicationEdition }),
      updatePublicationEdition: (
        publicationEditionParams: Partial<PublicationEditionModel>,
        entityOptions?: PublicationEditionRequestOptions,
      ) => (
        updatePublicationEdition({
          publicationEdition, publicationEditionParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deadlineStatuses,
    deadlines,
    deleting,
    hasArtworkDeadline,
    hasBookingDeadline,
    hasEditorialDeadline,
    hasOfiDeadline,
    loading: loading && !loadedIds?.includes(publicationEdition.id),
    publication,
    publicationDateObj,
    publicationEdition,
    updating,
    urls: generateUrls(publicationEdition),
  }
}

export default usePublicationEdition
